import { Component, Inject } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { ImageEntity } from "../../../../../shared/entities/file/image.entity";
import { BrandCampaignFacebookPost } from "../../../../../shared/models/brandCampaignFacebookPost";
import { BrandPost } from "../../../../../shared/models/brandCampaignPost";
import {
  CampaignPostContentType,
  LinkedInPostContentType,
  PostPlatform,
} from "../../../../../shared/enums/campaignPost.enums";
import { cloneWith } from "../../../../../shared/services/clonable";
import { DialogService } from "../../../../../shared/services/dialog.service";
import { BrandCampaignPostService } from "../../../shared/services/brand-campaign-post.service";

enum CommentContentType {
  Image,
  Text,
}

export interface CampaignContentPostCommentsDialogData {
  post: BrandPost;
  campaignDetailsId: number;
  categoryId: number;
}

export type CampaignContentPostCommentsDialogResult = BrandPost;

@Component({
  selector: "app-campaign-content-post-comments-dialog",
  templateUrl: "./campaign-content-post-comments-dialog.component.html",
  styleUrls: ["./campaign-content-post-comments-dialog.component.scss"],
})
export class CampaignContentPostCommentsDialogComponent {
  public saving = false;
  public autoSaving = false;
  public deleting = false;
  public showDeleteButton = false;
  public post: BrandPost;
  private campaignDetailsId: number;
  private categoryId: number;

  public readonly Platform = PostPlatform;
  public readonly ContentType = CampaignPostContentType;
  public readonly CommentType = CommentContentType;

  constructor(
    @Inject(MAT_DIALOG_DATA) data: CampaignContentPostCommentsDialogData,
    private readonly brandCampaignPostService: BrandCampaignPostService,
    private readonly dialogRef: MatDialogRef<
      CampaignContentPostCommentsDialogComponent,
      CampaignContentPostCommentsDialogResult
    >,
    private readonly dialogService: DialogService,
  ) {
    this.post = cloneWith(data.post);
    this.campaignDetailsId = data.campaignDetailsId;
    this.categoryId = data.categoryId;
    this.showDeleteButton =
      !!data.post.reviewImageComment || !!data.post.reviewTextComment;
  }

  public onSave(): void {
    this.saving = true;
    this.saveAction(true);
  }

  public saveAction(closeAfterSave = false): void {
    this.brandCampaignPostService
      .updateCampaignDetailCategoryPostReview(
        this.post.campaignId,
        this.campaignDetailsId,
        this.categoryId,
        this.post.id,
        {
          reviewImageComment: this.post.reviewImageComment,
          isReviewImageCommentResolved: this.post.isReviewImageCommentResolved,
          reviewTextComment: this.post.reviewTextComment,
          isReviewTextCommentResolved: this.post.isReviewTextCommentResolved,
          isReviewApproved: this.post.isReviewApproved,
        },
      )
      .subscribe({
        next: (post) => {
          this.post = post;

          if (closeAfterSave) {
            this.dialogRef.close(this.post);
          }

          this.saving = false;
          this.autoSaving = false;
        },
        error: () => {
          this.saving = false;
          this.autoSaving = false;
        },
      });
  }

  public onResolveComment(commentType: CommentContentType): void {
    this.setCommentState(commentType, true);
  }

  public onUnResolveComment(commentType: CommentContentType): void {
    this.setCommentState(commentType, false);
  }

  private setCommentState(
    commentType: CommentContentType,
    resolved: boolean,
  ): void {
    if (!this.saving) {
      this.autoSaving = true;

      if (commentType === CommentContentType.Image) {
        this.post.isReviewImageCommentResolved = resolved;
      } else {
        this.post.isReviewTextCommentResolved = resolved;
      }

      this.saveAction();
    }
  }

  public onDeleteCommentsConfirm(): void {
    this.dialogService.confirm({
      title: "brand.campaign.content.commentDialog.deleteConfirmationTitle",
      message: "brand.campaign.content.commentDialog.deleteConfirmation",
      onConfirm: () => this.onDeleteComments(),
    });
  }

  private onDeleteComments(): void {
    if (!this.saving) {
      this.saving = true;
      this.post.reviewImageComment = "";
      this.post.isReviewImageCommentResolved = false;
      this.post.reviewTextComment = "";
      this.post.isReviewTextCommentResolved = false;
      this.saveAction(true);
    }
  }

  public isSaveDisabled(): boolean {
    return this.saving || this.deleting;
  }

  public get isShownAsImagesCarousel(): boolean {
    const isFacebookCarousel =
      this.post.isFacebookPost &&
      this.post.mediaType === CampaignPostContentType.Carousel;
    const isLinkedInMultiImage =
      this.post.isLinkedInPost &&
      this.post.mediaType === LinkedInPostContentType.MultiImage;

    return this.post.isGoogleAd || isFacebookCarousel || isLinkedInMultiImage;
  }

  public getPostContentList(): ImageEntity[] {
    return this.post instanceof BrandCampaignFacebookPost
      ? this.post.carouselSlides
      : this.post.images;
  }
}
